import { syncViewportCanvasDisplay, VIEWPORT_RULER_THICKNESS, type ViewportCanvasMetrics } from './viewportFrame.js';
import type { ViewportResolution } from './viewportResolution.js';

const RULER_MAJOR_STEP = 100;
const RULER_MINOR_STEP = 10;
const RULER_MAJOR_TICK_LENGTH = 5;
const RULER_MINOR_TICK_LENGTH = 4;
const RULER_FONT_SIZE = 8;
const RULER_LABEL_TICK_GAP = 2;
const rulerDrawKeys = new WeakMap<HTMLCanvasElement, string>();

type PixelStyleProperty = 'left' | 'top' | 'width' | 'height';
type RulerStyleProperty = PixelStyleProperty | 'right' | 'bottom';

export function syncViewportRulers(size: ViewportResolution): void {
  const metrics = syncViewportCanvasDisplay(size);
  syncRulerCanvas(document.getElementById('viewport-ruler-x') as HTMLCanvasElement | null, size.w, 'x', metrics);
  syncRulerCanvas(document.getElementById('viewport-ruler-y') as HTMLCanvasElement | null, size.h, 'y', metrics);
}

function syncRulerCanvas(
  ruler: HTMLCanvasElement | null,
  size: number,
  axis: 'x' | 'y',
  metrics: ViewportCanvasMetrics,
): void {
  if (!ruler) return;

  let cssWidth = VIEWPORT_RULER_THICKNESS;
  let cssHeight = VIEWPORT_RULER_THICKNESS;
  if (axis === 'x') {
    cssWidth = Math.max(1, Math.round(metrics.w));
    setStylePx(ruler, 'left', metrics.x);
    setStylePx(ruler, 'top', Math.max(0, metrics.y - VIEWPORT_RULER_THICKNESS));
    setStylePx(ruler, 'width', cssWidth);
    setStylePx(ruler, 'height', cssHeight);
    setStyleValue(ruler, 'right', 'auto');
  } else {
    cssHeight = Math.max(1, Math.round(metrics.h));
    setStylePx(ruler, 'left', Math.max(0, metrics.x - VIEWPORT_RULER_THICKNESS));
    setStylePx(ruler, 'top', metrics.y);
    setStylePx(ruler, 'width', cssWidth);
    setStylePx(ruler, 'height', cssHeight);
    setStyleValue(ruler, 'bottom', 'auto');
  }

  drawRulerCanvas(ruler, size, axis, cssWidth, cssHeight);
}

function drawRulerCanvas(
  canvas: HTMLCanvasElement,
  size: number,
  axis: 'x' | 'y',
  cssWidth: number,
  cssHeight: number,
): void {
  const dpr = Math.max(1, window.devicePixelRatio || 1);
  const bitmapWidth = Math.max(1, Math.round(cssWidth * dpr));
  const bitmapHeight = Math.max(1, Math.round(cssHeight * dpr));
  const themeKey = getRulerThemeKey();
  const drawKey = [
    axis,
    size,
    cssWidth,
    cssHeight,
    bitmapWidth,
    bitmapHeight,
    themeKey,
    RULER_MAJOR_TICK_LENGTH,
    RULER_MINOR_TICK_LENGTH,
    RULER_FONT_SIZE,
    RULER_LABEL_TICK_GAP,
  ].join('|');

  if (rulerDrawKeys.get(canvas) === drawKey) return;
  rulerDrawKeys.set(canvas, drawKey);

  if (canvas.width !== bitmapWidth) canvas.width = bitmapWidth;
  if (canvas.height !== bitmapHeight) canvas.height = bitmapHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const styles = getComputedStyle(canvas);
  const color = styles.color || '#8585b2';
  const fontFamily = styles.fontFamily || 'monospace';

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssWidth, cssHeight);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1;
  ctx.font = `${RULER_FONT_SIZE}px ${fontFamily}`;
  ctx.textBaseline = 'bottom';

  const length = axis === 'x' ? cssWidth : cssHeight;
  if (size <= 0 || length <= 0) return;

  for (let value = RULER_MINOR_STEP; value < size; value += RULER_MINOR_STEP) {
    if (value % RULER_MAJOR_STEP === 0) continue;
    drawTick(ctx, axis, value, size, length, cssWidth, cssHeight, RULER_MINOR_TICK_LENGTH, 0.42);
  }

  for (let value = RULER_MAJOR_STEP; value < size; value += RULER_MAJOR_STEP) {
    drawTick(ctx, axis, value, size, length, cssWidth, cssHeight, RULER_MAJOR_TICK_LENGTH, 0.76);
    drawTickLabel(ctx, axis, value, size, length, cssWidth, cssHeight, color);
  }
}

function drawTick(
  ctx: CanvasRenderingContext2D,
  axis: 'x' | 'y',
  value: number,
  size: number,
  length: number,
  cssWidth: number,
  cssHeight: number,
  tickLength: number,
  alpha: number,
): void {
  const pos = Math.round((value / size) * length) + 0.5;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.beginPath();
  if (axis === 'x') {
    ctx.moveTo(pos, cssHeight - tickLength);
    ctx.lineTo(pos, cssHeight);
  } else {
    ctx.moveTo(cssWidth - tickLength, pos);
    ctx.lineTo(cssWidth, pos);
  }
  ctx.stroke();
  ctx.restore();
}

function drawTickLabel(
  ctx: CanvasRenderingContext2D,
  axis: 'x' | 'y',
  value: number,
  size: number,
  length: number,
  cssWidth: number,
  cssHeight: number,
  color: string,
): void {
  const pos = Math.round((value / size) * length);
  const label = String(value);
  ctx.save();
  ctx.globalAlpha = 0.9;
  ctx.fillStyle = color;
  if (axis === 'x') {
    ctx.textAlign = 'center';
    ctx.fillText(label, pos, cssHeight - RULER_MAJOR_TICK_LENGTH - RULER_LABEL_TICK_GAP);
  } else {
    ctx.translate(cssWidth - RULER_MAJOR_TICK_LENGTH - RULER_LABEL_TICK_GAP, pos);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.fillText(label, 0, 0);
  }
  ctx.restore();
}

function getRulerThemeKey(): string {
  const root = document.documentElement;
  const theme = root.getAttribute('data-theme') ?? '';
  const prefersLight = window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  return `${theme}|${prefersLight}`;
}

function setStylePx(element: HTMLElement, property: PixelStyleProperty, value: number): void {
  setStyleValue(element, property, `${Math.round(value)}px`);
}

function setStyleValue(element: HTMLElement, property: RulerStyleProperty, value: string): void {
  if (element.style[property] === value) return;
  element.style[property] = value;
}
